import {
	HoverCard,
	HoverCardType,
	IconButton,
	IPersonaProps,
	IPlainCardProps,
	Label,
	Persona,
	PersonaSize,
	Separator,
	Stack,
	useTheme,
} from '@fluentui/react';
import React from 'react';
import {Keyword, Market, User, Vko} from 'types';
import {ArrayValues, getHoverClassNames} from './EntityList/ColumnRenderers';
import {useTranslation} from 'react-i18next';
import {GetAllVkosQuery} from 'features/AdminSection/hooks/vko.generated';
import {v4 as uuidv4} from 'uuid';

interface Persona {
	name: string;
}

interface Props extends IPersonaProps {
	persons: Persona[];
}

export type VkoDetails = {
	userId: string;
	keywords: any[];
	markets: any[];
};

export type VexDetails = {
	userId: string;
	keywords: any[];
};

type KeywordWVexes = Keyword & {vexes: User[]};

export const VkosPersona: React.FC<{
	users: User[];
	keywords: Keyword[];
	dataVkos: GetAllVkosQuery | undefined;
}> = ({users, keywords, dataVkos}) => {
	const vkoDetailsAll: VkoDetails[] = React.useMemo(
		() => getVkoDetailsAll(keywords, (dataVkos?.vkos ?? []) as Vko[]),
		[keywords, dataVkos],
	);
	return (
		<>
			{users.map(user => {
				const propsVkoPersona = {user, vkoDetailsAll};
				return <VkoPersona key={uuidv4()} {...propsVkoPersona} />;
			})}
		</>
	);
};

export const VexesPersona: React.FC<{
	users: User[];
	keywords: Keyword[];
}> = ({users, keywords}) => {
	const vexDetailsAll: VexDetails[] = React.useMemo(
		() => getVexDetailsAll(keywords),
		[keywords],
	);

	return (
		<>
			{users.map(user => {
				const propsVexPersona = {user, vexDetailsAll};
				return <VexPersona key={uuidv4()} {...propsVexPersona} />;
			})}
		</>
	);
};

const VkoPersona: React.FC<{
	user: User;
	vkoDetailsAll: VkoDetails[];
}> = ({user, vkoDetailsAll}) => {
	const {t} = useTranslation('components/persona');

	const theme = useTheme();
	const classnames = getHoverClassNames(theme);

	const vkoDetails = vkoDetailsAll.find(x => x.userId === user.id);
	const keywordsVko = vkoDetails?.keywords ?? [];
	const marketsVkoDefault = [
		t('AllMarkets', {
			keyPrefix: 'VKO',
		}),
	];
	const marketsVko =
		vkoDetails?.markets && vkoDetails?.markets.length > 0
			? vkoDetails?.markets
			: marketsVkoDefault;

	const detailClusters = (
		<>
			{getDetailsCluster(
				t('Keywords', {
					keyPrefix: 'VKO',
				}),
				keywordsVko,
				classnames.detailCluster,
			)}
			{getDetailsCluster(
				t('Markets', {
					keyPrefix: 'VKO',
				}),
				marketsVko,
				classnames.detailCluster,
			)}
		</>
	);

	const propsHoverablePersona = {user, detailClusters};
	return <HoverablePersona {...propsHoverablePersona} />;
};

const VexPersona: React.FC<{
	user: User;
	vexDetailsAll: VexDetails[];
}> = ({user, vexDetailsAll}) => {
	const {t} = useTranslation('components/persona');

	const theme = useTheme();
	const classnames = getHoverClassNames(theme);

	const vexDetails = vexDetailsAll.find(x => x.userId === user.id);
	const keywordsVex = vexDetails?.keywords ?? [];

	const detailClusters = (
		<>
			{getDetailsCluster(
				t('Keywords', {
					keyPrefix: 'VKO',
				}),
				keywordsVex,
				classnames.detailCluster,
			)}
		</>
	);

	const propsHoverablePersona = {user, detailClusters};
	return <HoverablePersona {...propsHoverablePersona} />;
};

const HoverablePersona: React.FC<{
	user: User;
	detailClusters: JSX.Element;
}> = ({user, detailClusters}) => {
	const {t} = useTranslation('components/persona');

	const theme = useTheme();
	const classnames = getHoverClassNames(theme);

	const handleClick = (href: string) => () => {
		window.location.href = href;
	};

	const email = user.mail ?? '';
	const hoverButtons = [
		{
			name: 'Message',
			href: `msteams://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(
				email,
			)}`,
		},
		{
			name: 'Mail',
			href: `mailto:${email}`,
		},
		{
			name: 'Phone',
			href: `msteams://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(
				email,
			)}`,
		},
	];

	const hoverPropsDefault: IPlainCardProps = {
		onRenderPlainCard: () => (
			<Stack className={classnames.hover} tokens={{childrenGap: 5}}>
				<Label>{user.name}</Label>
				<Stack horizontal tokens={{childrenGap: 5}}>
					{hoverButtons.map((x, i) => (
						<IconButton
							key={i}
							title={t(x.name, {userName: user.name})}
							iconProps={{iconName: x.name}}
							onClick={handleClick(x.href)}
						/>
					))}
				</Stack>
				{detailClusters}
			</Stack>
		),
	};

	const propsPersona = {
		size: PersonaSize.size8,
	};
	return (
		<HoverCard type={HoverCardType.plain} plainCardProps={hoverPropsDefault}>
			<Persona text={user.name} {...propsPersona} />
		</HoverCard>
	);
};

export function Personas({persons, ...other}: Props): JSX.Element {
	const renderPersona = (persona: Persona, index: number): JSX.Element => (
		<Persona key={index} text={persona.name} {...other} />
	);

	const renderedPersonas: JSX.Element[] = persons.map(renderPersona);

	return <>{renderedPersonas}</>;
}

export const getVkoDetailsAll = (keywords: Keyword[], vkos: Vko[]) => {
	const getUsers = (keyword: Keyword) => keyword.vkos;

	return transfromArray(keywords, getUsers).map(x => ({
		...x,
		markets: (vkos.find(y => y.vkoUser.id === x.userId)?.markets ??
			[]) as Market[],
	}));
};

export const getVexDetailsAll = (keywords: Keyword[]) => {
	const getUsers = (keyword: Keyword) => (keyword as KeywordWVexes).vexes;

	return transfromArray(keywords, getUsers);
};

const transfromArray = (
	keywords: Keyword[],
	getUsers: (keyword: Keyword) => User[],
) => {
	const mapKeyword = new Map<string, string>();
	const mapUserKeyword = new Map<string, Set<string>>();
	for (const keyword of keywords) {
		mapKeyword.set(keyword.id, keyword.name);
		const users = getUsers(keyword as KeywordWVexes);
		for (const user of users) {
			if (!mapUserKeyword.has(user.id)) {
				mapUserKeyword.set(user.id, new Set());
			}
			mapUserKeyword.get(user.id)!.add(keyword.id);
		}
	}
	return Array.from(mapUserKeyword, ([userId, keywordIds]) => ({
		userId,
		keywords: Array.from(keywordIds).map(x => ({
			id: x,
			name: mapKeyword.get(x),
		})) as Keyword[],
	}));
};

const getDetailsCluster = (
	heading: string,
	items: any[],
	className: string,
) => {
	return (
		<div className={className}>
			{heading}
			<Separator />
			<ArrayValues array={items}></ArrayValues>
		</div>
	);
};

export const getUserDepartment = (user: User) => {
	const start = user.name.lastIndexOf('(');
	const end = user.name.lastIndexOf(')');
	return user.name.slice(start, end);
};
